import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { useTrainings } from "../../../../../hooks/useTrainings"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import {
  createTrainingValidationSchema,
  useTrainingFieldRenderers,
} from "../../../../components/Helpers"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { TrainingGet, TrainingPut } from "../../../../models/Training"

export function TrainingDetails(props: {
  readonly training: TrainingGet
  readonly setTraining: (updatedTraining: TrainingGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { training, setTraining } = props
  const { trainingsList } = useTrainings()

  const validationSchema = useMemo(
    () =>
      createTrainingValidationSchema(
        trainingsList.filter((t) => t.id !== training.id).map((t) => t.name)
      ),
    [trainingsList, training.id]
  )

  const formMethods = useForm<TrainingPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: training.name,
      title: training.title,
      description: training.description,
      imageUrl: training.imageUrl,
      deviceImageUrl: training.deviceImageUrl,
      summary: training.summary,
      disclaimer: training.disclaimer,
      completionMessage: training.completionMessage,
      validityMonths: training.validityMonths,
      expiryReminderDays: training.expiryReminderDays,
    })
  }, [training, reset])

  const onSubmit = useCallback(
    (values: TrainingPut) =>
      digitalManagerApi
        .put<TrainingGet>(`/trainings/${training.id}`, {
          name: values.name,
          title: values.title,
          description: values.description,
          imageUrl: values.imageUrl,
          deviceImageUrl: values.deviceImageUrl,
          summary: values.summary,
          disclaimer: values.disclaimer,
          completionMessage: values.completionMessage,
          validityMonths: values.validityMonths,
          expiryReminderDays: values.expiryReminderDays,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Training updated")
            setTraining(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update training")
          throw new Error("Cannot update training")
        }),
    [training.id, setTraining]
  )

  const { renderTextField, renderNumberField } = useTrainingFieldRenderers({ errors })

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<TrainingPut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      training.application.id,
      DimaFeatureShortNameEnum.TM,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{training.name}</div>,
        editItem: <Controller name="name" control={control} render={renderTextField} />,
      },
      {
        id: 1,
        label: "Title",
        displayItem: <div className="title">{training.title}</div>,
        editItem: <Controller name="title" control={control} render={renderTextField} />,
      },
      {
        id: 2,
        label: "Description",
        displayItem: <div className="title">{training.description}</div>,
        editItem: (
          <Controller name="description" control={control} render={renderTextField} />
        ),
      },
      {
        id: 3,
        label: "Image URL",
        displayItem: <div className="title">{training.imageUrl}</div>,
        editItem: (
          <Controller name="imageUrl" control={control} render={renderTextField} />
        ),
      },
      {
        id: 4,
        label: "Device image URL",
        displayItem: <div className="title">{training.deviceImageUrl}</div>,
        editItem: (
          <Controller name="deviceImageUrl" control={control} render={renderTextField} />
        ),
      },
      {
        id: 5,
        label: "Summary",
        displayItem: <div className="title">{training.summary}</div>,
        editItem: (
          <Controller name="summary" control={control} render={renderTextField} />
        ),
      },
      {
        id: 6,
        label: "Disclaimer",
        displayItem: <div className="title">{training.disclaimer}</div>,
        editItem: (
          <Controller name="disclaimer" control={control} render={renderTextField} />
        ),
      },
      {
        id: 7,
        label: "Completion message",
        displayItem: <div className="title">{training.completionMessage}</div>,
        editItem: (
          <Controller
            name="completionMessage"
            control={control}
            render={renderTextField}
          />
        ),
      },
      {
        id: 8,
        label: "Validity months",
        displayItem: <div className="title">{training.validityMonths}</div>,
        editItem: (
          <Controller
            name="validityMonths"
            control={control}
            render={renderNumberField}
          />
        ),
      },
      {
        id: 9,
        label: "Expiry reminder days",
        displayItem: <div className="title">{training.expiryReminderDays ?? ""}</div>,
        editItem: (
          <Controller
            name="expiryReminderDays"
            control={control}
            render={renderNumberField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
